import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {Observable} from 'rxjs';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {shareReplay} from 'rxjs/operators';
import {ExternalUrlPage} from '@models/sidebar/response/external-url-page';
import {PageItem} from '@models/sidebar/page-item';
import {PAGE_CONTAINER_TYPE} from '@constants/sidebar/page-type.constant';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {NotificationService} from '@services/notification/notification.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {TranslateService} from '@ngx-translate/core';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {FavoriteNotificationService} from '@services/left-menu/favorites/favorite-notification/favorite-notification.service';
import {UpdateUrlService} from '@services/update-url/update-url.service';
import {BaseMenuItem} from '@models/sidebar/response/base-menu-item';

@Injectable({
  providedIn: 'root'
})
export class ExternalUrlService {

  private GET_EXTERNAL_URL_PAGE_URL_SUFFIX = '/api/web/base/:machineOrGroupId/externalUrlPage/:tabId/:isParentMachineGroup';
  private CREATE_EXTERNAL_URL_PAGE_URL_SUFFIX = '/api/web/base/:machineOrGroupId/externalUrlPage';
  private DELETE_EXTERNAL_URL_PAGE_URL_SUFFIX = '/api/web/base/:machineOrGroupId/externalUrlPage/:tabId/baseId/:itemId/:isParentMachineGroup';
  private UPDATE_EXTERNAL_URL_PAGE_URL_SUFFIX = '/api/web/base/:machineOrGroupId/externalUrlPage/update/baseId/:itemId';

  private readonly savingIndicatorMessage = '';
  private readonly errorCreatingPage = '';
  private readonly successCreatingPage = '';
  private readonly errorRenamingPage = '';
  private readonly successRenamingPage = '';

  private readonly BASE_URL: string;

  constructor(private http: HttpClient,
              private apiLocation: ApiLocationService,
              private loadingIndicator: LoadingIndicatorService,
              private leftMenuService: LeftMenuService,
              private notificationService: NotificationService,
              private menuNotificationService: MenuNotificationService,
              private translateService: TranslateService,
              private favoriteNotificationService: FavoriteNotificationService,
              private updateUrlService: UpdateUrlService) {
    this.BASE_URL = this.apiLocation.getLocation();
    this.savingIndicatorMessage = this.translateService.instant('Saving');
    this.errorCreatingPage = this.translateService.instant('Error creating external url page.');
    this.successCreatingPage = this.translateService.instant('External url page was created.');
    this.errorRenamingPage = this.translateService.instant('Error renaming external url page.');
    this.successRenamingPage = this.translateService.instant('External url page was renamed.');
  }

  public getExternalUrl(machineOrGroupId: string, isParentMachineGroup: boolean, tabId: string): Observable<any> {
    let url: string = this.BASE_URL + this.GET_EXTERNAL_URL_PAGE_URL_SUFFIX;
    url = url.replace(':machineOrGroupId', machineOrGroupId)
    .replace(':tabId', tabId)
    .replace(':isParentMachineGroup', String(isParentMachineGroup));

    const response = this.http.get(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
    response.subscribe((result) => {
    });
    return response;
  }

  public create(baseMenuItems: PageItem[], pageItem: PageItem, machineOrGroupId: string, currentPageName: string, parentId: string | null, isAdminOrHigher: boolean, isMachineTab: boolean): void {
    if (parentId && pageItem.url) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const id = this.leftMenuService.getHighestPageIdNumber(baseMenuItems, pageItem);
      const pageId = PAGE_CONTAINER_TYPE.EXTERNAL_URL_PAGE + id;
      pageItem.pageId = pageId;
      const externalUrl = new ExternalUrlPage(pageId, pageItem.name, isMachineTab, pageItem.url);
      this.createExternalUrl(machineOrGroupId, externalUrl).subscribe((newBaseMenuItem: BaseMenuItem) => {
        this.handleCreatedPage(newBaseMenuItem, pageItem, baseMenuItems, currentPageName, parentId, isAdminOrHigher);
      }, (error) => {
        this.notificationService.error(this.errorCreatingPage, error, currentPageName, true);
        this.loadingIndicator.hide();
      });
    }
  }

  private createExternalUrl(machineOrGroupId: string, request: ExternalUrlPage): Observable<any> {
    let url: string = this.BASE_URL + this.CREATE_EXTERNAL_URL_PAGE_URL_SUFFIX;
    url = url.replace(':machineOrGroupId', machineOrGroupId);
    return this.http.post<ExternalUrlPage>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  private handleCreatedPage(createdItem: BaseMenuItem, pageItem: PageItem, baseMenuItems: PageItem[], currentPageName: string, parentId: string, isAdminOrHigher: boolean) {
    this.menuNotificationService.broadcast(new MenuNotification(OperationType.ADD, parentId, createdItem.name, ''));
    pageItem.id = createdItem.id;
    pageItem.canBeAltered = isAdminOrHigher;
    pageItem.parent = createdItem.parent;
    pageItem.pageId = createdItem.pageId;
    pageItem.index = createdItem.index;
    baseMenuItems.push(pageItem);
    this.loadingIndicator.hide();
    this.notificationService.success(this.successCreatingPage, currentPageName, false);
  }

  public deleteExternalUrl(machineOrGroupId: string, tabId: string, itemId: string, isParentMachineGroup: boolean): Observable<any> {
    let url: string = this.BASE_URL + this.DELETE_EXTERNAL_URL_PAGE_URL_SUFFIX;
    url = url.replace(':machineOrGroupId', machineOrGroupId)
    .replace(':tabId', tabId)
    .replace(':itemId', itemId)
    .replace(':isParentMachineGroup', String(isParentMachineGroup));
    return this.http.delete<ExternalUrlPage>(url, HTTPS_REQUEST_OPTIONS);
  }

  public update(pageItem: PageItem, machineOrGroupId: string, parentId: string | null, currentPageName: string, isMachineTab: boolean, nameFallback: Object): void {
    if (parentId && pageItem.id && pageItem.pageId && pageItem.url) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const externalUrl = new ExternalUrlPage(pageItem.pageId, pageItem.name, isMachineTab, pageItem.url);
      const itemId: string = pageItem.id;
      this.updateExternalUrl(machineOrGroupId, itemId, externalUrl).subscribe((result: BaseMenuItem) => {
        this.handleUpdatedPage(result.name, parentId, currentPageName, itemId, pageItem);
      }, (error) => {
        this.leftMenuService.broadcastErrorUpdatePageChange(nameFallback);
        this.notificationService.error(this.errorRenamingPage, error, currentPageName, true);
        this.loadingIndicator.hide();
      });
    }
  }

  private updateExternalUrl(machineOrGroupId: string, itemId: string, request: ExternalUrlPage): Observable<any> {
    let url: string = this.BASE_URL + this.UPDATE_EXTERNAL_URL_PAGE_URL_SUFFIX;
    url = url.replace(':machineOrGroupId', machineOrGroupId).replace(':itemId', itemId);
    return this.http.post<ExternalUrlPage>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  private handleUpdatedPage(updatedName: string, parentId: string, currentPageName: string, itemId: string, pageItem: PageItem) {
    this.menuNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_INSIDE_PARENT, parentId, '', updatedName));
    this.favoriteNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_NAME, itemId, '', updatedName));
    this.updateUrlService.broadcast(pageItem);
    this.loadingIndicator.hide();
    this.notificationService.success(this.successRenamingPage, currentPageName, false);
  }

}
